/** A person always sees the results of what they ran (item 70; Segev on
 *  staging: a run answered 297 conversations of May-Sep, the page opened on
 *  October and showed one). Pure functions, no React - the Templates tab and
 *  its dashboard apply them; `tests/unit/insights/template_range.test.mjs`
 *  runs the real code.
 *
 *  The rule:
 *  - every chart says what its date range leaves out ("N answers are outside
 *    this date range - Show all"), and Show all widens the range to the days
 *    the answers cover (the server's `span`, by the same activity rule the
 *    charts filter by - so the widened range holds every one of them);
 *  - while the person has not chosen dates themselves (the page's default
 *    range), opening the Templates tab widens it the same way, once;
 *  - a run that finishes widens the range to the days ITS answers cover,
 *    whatever was chosen - the person just asked for those answers.
 *  The range only ever grows here (it is the page's one range: the other
 *  tabs read it too, and say so in their own selector). */

import type { DaySpan } from '../types/insights';

export interface DayRange { dateFrom: string; dateTo: string }
/** The days a template's answers cover, as YYYY-MM-DD (UTC days, as the server filters by). */
export type Span = DaySpan;

export interface RangeFacts {
  /** Answers in the scope that this range leaves out. */
  outside?: number;
  span?: Span | null;
}

/** The smallest range that holds `range` and `span` (and, when given, today:
    a widened range keeps running to now, so newer answers stay in it). */
export function cover(range: DayRange, span: Span | null | undefined, today?: string): DayRange {
  if (!span) return range;
  const ends = [range.dateTo, span.last, ...(today ? [today] : [])].sort();
  return {
    dateFrom: [range.dateFrom, span.first].sort()[0],
    dateTo: ends[ends.length - 1],
  };
}

/** Several templates' spans as one (the dashboard: every card at once). */
export function joinSpans(spans: Array<Span | null | undefined>): Span | null {
  const known = spans.filter((s): s is Span => !!s && !!s.first && !!s.last);
  if (!known.length) return null;
  return {
    first: known.map((s) => s.first).sort()[0],
    last: known.map((s) => s.last).sort()[known.length - 1],
  };
}

/** What a set of charts leaves out, together: the answers outside the range
    and the span that would show them all. */
export function outsideOf(facts: RangeFacts[]): { outside: number; span: Span | null } {
  return {
    outside: facts.reduce((sum, f) => sum + (f.outside || 0), 0),
    span: joinSpans(facts.filter((f) => (f.outside || 0) > 0).map((f) => f.span)),
  };
}

/** Whether a range already holds a span (nothing to widen). */
export function holds(range: DayRange, span: Span | null | undefined): boolean {
  return !span || (range.dateFrom <= span.first && range.dateTo >= span.last);
}

/** The range to open on: the default one widened to the answers, while the
    person has not chosen dates (`chosen`) and something lies outside it. */
export function openingRange(range: DayRange, chosen: boolean, facts: RangeFacts[], today: string): DayRange | null {
  if (chosen) return null;
  const { outside, span } = outsideOf(facts);
  if (!outside || holds(range, span)) return null;
  return cover(range, span, today);
}
